import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatMoney } from '../utils/currency';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import PromptDialog from '../components/common/PromptDialog';
import RecordPayoutDialog from '../components/payouts/RecordPayoutDialog';
import { useAuth } from '../context/AuthContext';
import { canAny } from '../hooks/usePermissions';

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Requested', value: 'requested' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Paid', value: 'paid' },
];

const Payouts = () => {
  const { admin } = useAuth();
  const canManagePayouts = canAny(admin, ['manage_payouts', 'manage_payments']);

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [actionLoading, setActionLoading] = useState(false);
  const [recordPayoutOpen, setRecordPayoutOpen] = useState(false);

  // Dialog state
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);

  const driverSearchDebounce = useRef(null);

  const fetchPayouts = useCallback(async (page = 1, status = statusFilter, search = driverSearch) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (status) params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`/admin/payouts?${params.toString()}`);
      setPayouts(response.data.data);
      setPagination(response.data.pagination);
      if (response.data.currencyCode) {
        setCurrencyCode(String(response.data.currencyCode).toUpperCase());
      }
    } catch (error) {
      toast.error('Failed to fetch payouts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, driverSearch]);

  useEffect(() => {
    fetchPayouts(1, statusFilter);
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce driver search input
  const handleDriverSearchChange = (e) => {
    const val = e.target.value;
    setDriverSearch(val);
    clearTimeout(driverSearchDebounce.current);
    driverSearchDebounce.current = setTimeout(() => {
      fetchPayouts(1, statusFilter, val);
    }, 350);
  };

  const handleApproveConfirm = async () => {
    const payout = approveTarget;
    if (!payout?._id) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/payouts/${payout._id}/approve`);
      toast.success('Payout approved');
      setApproveTarget(null);
      fetchPayouts(pagination.page);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to approve payout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async ({ reason }) => {
    const payout = rejectTarget;
    if (!payout?._id) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/payouts/${payout._id}/reject`, { reason: reason || undefined });
      toast.success('Payout rejected');
      setRejectTarget(null);
      fetchPayouts(pagination.page);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reject payout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaidConfirm = async () => {
    const payout = markPaidTarget;
    if (!payout?._id) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/payouts/${payout._id}/mark-paid`);
      toast.success('Payout marked as paid');
      setMarkPaidTarget(null);
      fetchPayouts(pagination.page);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to mark payout as paid');
    } finally {
      setActionLoading(false);
    }
  };

  const renderActions = (payout) => {
    if (payout.status === 'requested') {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => setApproveTarget(payout)}
            className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Approve
          </button>
          <button
            onClick={() => setRejectTarget(payout)}
            className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
          >
            Reject
          </button>
        </div>
      );
    }
    if (payout.status === 'approved') {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => setMarkPaidTarget(payout)}
            className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
          >
            Mark Paid
          </button>
          <button
            onClick={() => setRejectTarget(payout)}
            className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
          >
            Reject
          </button>
        </div>
      );
    }
    return <span className="text-sm text-gray-400">—</span>;
  };

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Driver Payouts</h1>
        {canManagePayouts && (
          <button
            onClick={() => setRecordPayoutOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            + Record Payout
          </button>
        )}
      </div>

      {/* Toolbar: driver search + status tabs */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <input
          type="text"
          value={driverSearch}
          onChange={handleDriverSearchChange}
          placeholder="Filter by driver name or phone…"
          className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              statusFilter === tab.value
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Driver Name</th>
                  <th className="table-header">Phone</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Method</th>
                  <th className="table-header">Bank Ref</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payouts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="table-cell text-center text-gray-500 py-8">
                      No payouts found
                    </td>
                  </tr>
                ) : (
                  payouts.map((payout) => (
                    <tr key={payout._id} className="hover:bg-gray-50">
                      <td className="table-cell font-medium">
                        {payout.driver ? `${payout.driver.firstName} ${payout.driver.lastName}` : 'N/A'}
                      </td>
                      <td className="table-cell text-sm text-gray-600">
                        {payout.driver?.phone || 'N/A'}
                      </td>
                      <td className="table-cell font-medium">
                        {formatMoney(payout.amount || 0, payout.currency || currencyCode)}
                      </td>
                      <td className="table-cell text-sm">
                        {payout.method === 'manual'
                          ? <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">Manual</span>
                          : <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">Driver</span>}
                      </td>
                      <td className="table-cell text-sm text-gray-600 max-w-[140px] truncate" title={payout.bankReference || ''}>
                        {payout.bankReference || '—'}
                      </td>
                      <td className="table-cell"><StatusBadge status={payout.status} /></td>
                      <td className="table-cell text-sm text-gray-500">
                        {new Date(payout.paidAt || payout.createdAt).toLocaleString()}
                      </td>
                      <td className="table-cell">{renderActions(payout)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pagination.page}
            pages={pagination.pages}
            onPageChange={(p) => fetchPayouts(p)}
          />
        </div>
      )}

      {/* Approve confirm */}
      <ConfirmDialog
        isOpen={!!approveTarget}
        title="Approve Payout"
        message={approveTarget ? `Approve payout of ${formatMoney(approveTarget.amount, approveTarget.currency || currencyCode)} for ${approveTarget.driver?.firstName} ${approveTarget.driver?.lastName}?` : ''}
        confirmLabel="Approve"
        variant="primary"
        loading={actionLoading}
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveTarget(null)}
      />

      {/* Reject with optional reason */}
      <PromptDialog
        isOpen={!!rejectTarget}
        title="Reject Payout"
        fields={[{ name: 'reason', label: 'Rejection reason (optional)', type: 'text', placeholder: '' }]}
        confirmLabel="Reject Payout"
        loading={actionLoading}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectTarget(null)}
      />

      {/* Mark paid confirm */}
      <ConfirmDialog
        isOpen={!!markPaidTarget}
        title="Mark as Paid"
        message="Mark this payout as paid? This will debit the driver's ledger balance."
        confirmLabel="Mark Paid"
        variant="primary"
        loading={actionLoading}
        onConfirm={handleMarkPaidConfirm}
        onCancel={() => setMarkPaidTarget(null)}
      />

      {/* Record payout (admin-initiated payroll) */}
      <RecordPayoutDialog
        isOpen={recordPayoutOpen}
        onClose={() => setRecordPayoutOpen(false)}
        onRecorded={() => {
          setStatusFilter('paid');
          fetchPayouts(1, 'paid', driverSearch);
        }}
        currencyCode={currencyCode}
      />
    </div>
  );
};

export default Payouts;
